import { createContext, useContext, ReactNode, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

interface RoomSettingsState {
  boardReadOnly: boolean;
  todoReadOnly: boolean;
  notesReadOnly: boolean;
}

interface RoomSettingsContextType extends RoomSettingsState {
  isLoading: boolean;
}

const RoomSettingsContext = createContext<RoomSettingsContextType | undefined>(undefined);

interface RoomSettingsProviderProps {
  roomId: string;
  children: ReactNode;
}

// Always return false for all read-only flags to allow editing for everyone
const fetchRoomSettings = async (): Promise<RoomSettingsState> => {
  return { 
    boardReadOnly: false, 
    todoReadOnly: false, 
    notesReadOnly: false 
  };
};

export const RoomSettingsProvider = ({ roomId, children }: RoomSettingsProviderProps) => {
  const queryClient = useQueryClient();

  // Always allow editing for everyone
  const { data, isLoading } = useQuery<RoomSettingsState>({
    queryKey: ['roomSettings'],
    queryFn: fetchRoomSettings,
    staleTime: Infinity, // Never stale since we're hardcoding the values
  });

  // No realtime subscription needed since we're hardcoding the values

  return (
    <RoomSettingsContext.Provider
      value={{
        boardReadOnly: data?.boardReadOnly ?? false,
        todoReadOnly: data?.todoReadOnly ?? false,
        notesReadOnly: data?.notesReadOnly ?? false,
        isLoading,
      }}
    >
      {children}
    </RoomSettingsContext.Provider>
  );
};

export const useRoomSettings = () => {
  const ctx = useContext(RoomSettingsContext);
  if (!ctx) throw new Error('useRoomSettings must be used within RoomSettingsProvider');
  return ctx;
};
